import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { GraphQLResponse, ProtoApiService } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { Date as DateModel } from '@lsq-frontend/remote-services/all-java/common-mk0';
import { PayablesInput, PayablesResponse, EnumInput } from '../../model/models';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { isNil, omitBy } from 'lodash';
import { ListGroupedInvoicePayLaterEligibleForBuyerRequest } from './request/list-grouped-invoice-pay-later-eligible-for-buyer-request';
import { ListInvoicePayLaterEligibleForBuyerResponse } from './response/list-grouped-invoice-pay-later-eligible-for-buyer-response';
import { convertDateToLocalDate } from '../../helpers/date-mapping';

@Injectable({ providedIn: 'root' })
export class ListGroupedInvoicePayLaterEligibleForBuyerService extends ProtoApiService<
    ListGroupedInvoicePayLaterEligibleForBuyerRequest,
    ListInvoicePayLaterEligibleForBuyerResponse
> {
    public endpointName: string;
    public requestType = ListGroupedInvoicePayLaterEligibleForBuyerRequest;
    public responseType = ListInvoicePayLaterEligibleForBuyerResponse;

    constructor(http: HttpClient, @Inject(SETTINGS) settings: ISettings) {
        super(http, settings);
        this.endpointName = 'v1/query';
        this.serviceName = 'financingGql';
        this.usePascalForUrl = false;
    }

    public requestMapper(req: ListGroupedInvoicePayLaterEligibleForBuyerRequest): {
        query: string;
    } {
        const statuses = req.buyerInvoiceStatus.map((status) => {
            return new EnumInput(status);
        });
        const input: PayablesInput = omitBy(
            {
                payableIds: [+req.refInvoiceKey],
                statuses: statuses,
                companyFilter: {
                    buyerIds: [req.buyerKey]
                }
            } as PayablesInput,
            isNil
        );

        const query = jsonToGraphQLQuery({
            payables: {
                __args: { input },
                rows: {
                    id: true,
                    status: true,
                    statusLegacy: true,
                    originalDueDate: true,
                    dueInDays: true,
                    buyerDiscountFeeRate: true,
                    buyerDiscountFee: { cents: true, currencyCode: true },
                    supplierDiscountFee: { cents: true, currencyCode: true },
                    termsExtensionDays: true,
                    relationship: {
                        supplier: { company: { name: true } },
                        buyer: { id: true, company: { name: true } }
                    },
                    approvals: {
                        id: true,
                        amount: { cents: true, currencyCode: true },
                        amountLessSupplierDiscountFee: { cents: true, currencyCode: true },
                        adjustedDueDate: true,
                        invoice: {
                            date: true,
                            invoiceNumber: true,
                            amount: { cents: true, currencyCode: true }
                        }
                    },
                    creditMemos: {
                        id: true,
                        creditMemoNumber: true,
                        date: true,
                        amount: { cents: true, currencyCode: true },
                        allocatedAmount: { cents: true, currencyCode: true }
                    }
                },
                total: { payableCount: true }
            }
        });

        return { query: `{${query}}` };
    }

    public responseMapper(
        res: GraphQLResponse<PayablesResponse>
    ): ListInvoicePayLaterEligibleForBuyerResponse {
        const { rows: payables, total } = (res as GraphQLResponse<PayablesResponse>).data.payables;
        const payable = payables[0];
        const { buyer, supplier } = payable.relationship;

        const invoiceDueDate = convertDateToLocalDate(payable.originalDueDate);
        const invoiceDueDateNew =
            invoiceDueDate &&
            new DateModel({
                day: invoiceDueDate.getDate(),
                month: invoiceDueDate.getMonth() + 1,
                year: invoiceDueDate.getFullYear()
            });

        const mappedResp = new ListInvoicePayLaterEligibleForBuyerResponse({
            ok: true,
            totalCount: total.payableCount,
            rows: [
                ...payable.creditMemos.map((cm) => {
                    let creditMemoAmount: number;

                    if (cm.allocatedAmount.cents == 0) {
                        creditMemoAmount = cm.amount.cents;
                    } else {
                        creditMemoAmount = cm.allocatedAmount.cents;
                    }

                    if (creditMemoAmount > 0) {
                        creditMemoAmount = creditMemoAmount * -1;
                    }

                    const invoiceDate = convertDateToLocalDate(cm.date);
                    const invoiceDateNew =
                        invoiceDate &&
                        new DateModel({
                            day: invoiceDate.getDate(),
                            month: invoiceDate.getMonth() + 1,
                            year: invoiceDate.getFullYear()
                        });

                    return {
                        isGroup: false,
                        invoiceDate,
                        invoiceDateNew,
                        invoiceDueDate,
                        invoiceDueDateNew,
                        refInvoiceKey: payable.id,
                        refGroupedInvoiceKey: cm.id,
                        invoiceNumber: cm.creditMemoNumber,
                        dueIn: payable.dueInDays,
                        termsExtensionDays: payable.termsExtensionDays,
                        sellerName: supplier?.company.name,
                        invoiceAmount: {
                            cents: creditMemoAmount,
                            currencyCode: cm.amount.currencyCode
                        }
                    } as any;
                }),
                ...payable.approvals.map((approval) => {
                    const invoiceDate = convertDateToLocalDate(approval.invoice.date);
                    const invoiceDateNew =
                        invoiceDate &&
                        new DateModel({
                            day: invoiceDate.getDate(),
                            month: invoiceDate.getMonth() + 1,
                            year: invoiceDate.getFullYear()
                        });

                    const invoiceDueDate = convertDateToLocalDate(approval.adjustedDueDate);
                    const invoiceDueDateNew =
                        invoiceDueDate &&
                        new DateModel({
                            day: invoiceDueDate.getDate(),
                            month: invoiceDueDate.getMonth() + 1,
                            year: invoiceDueDate.getFullYear()
                        });

                    return {
                        isGroup: false,
                        invoiceDate,
                        invoiceDateNew,
                        invoiceDueDate,
                        invoiceDueDateNew,
                        refInvoiceKey: payable.id,
                        refGroupedInvoiceKey: approval.id,
                        invoiceNumber: approval.invoice.invoiceNumber,
                        dueIn: payable.dueInDays,
                        termsExtensionDays: payable.termsExtensionDays,
                        sellerName: supplier?.company.name,
                        invoiceAmount: approval.invoice.amount // TODO: Summing payable.approvals[].invoice.amount would be more correct
                    } as any;
                })
            ]
        });
        return mappedResp;
    }

    public get endpointUrl(): string {
        return super.endpointUrl.replace('/api', '');
    }
}
